import homeHero from '@/assets/images/public-pages/home-hero.png';
import benefitsHero from '@/assets/images/public-pages/benefits-hero.png';
import homeOurMission from '@/assets/images/public-pages/home-our-mission.png';
import homeAiDueDiligence from '@/assets/images/public-pages/home-ai-due-diligence.png';
import homeForInvestors from '@/assets/images/public-pages/home-for-investors.png';
import homeForTrustedPartners from '@/assets/images/public-pages/home-for-trusted-partners.png';
import benefitsPrepareWithPurpose from '@/assets/images/public-pages/benefits-prepare-with-purpose.png';
import homeFundraisingCard0 from '@/assets/images/public-pages/home-fundraising-card-0.png';
import homeFundraisingCard1 from '@/assets/images/public-pages/home-fundraising-card-1.png';
import homeFundraisingCard2 from '@/assets/images/public-pages/home-fundraising-card-2.png';
import homeFundraisingCard3 from '@/assets/images/public-pages/home-fundraising-card-3.png';
import investorsHero from '@/assets/images/public-pages/investors-hero.png';
import investorsBenefitsTopLeft from '@/assets/images/public-pages/investors-benefits-1-inner.jpg';
import investorsBenefitsTopRight from '@/assets/images/public-pages/investors-benefits-2-inner.jpg';
import investorsBenefitsBottom from '@/assets/images/public-pages/investors-benefits-3-inner.jpg';
import trustedPartnersHero from '@/assets/images/public-pages/trusted-partners-hero.png';
import trustedPartnersCategoriesBg from '@/assets/images/public-pages/trusted-partners-categories-bg.png';
import iconCoBranded from '@/assets/images/public-pages/trusted-partners-icon-co-branded.svg';
import iconEducation from '@/assets/images/public-pages/trusted-partners-icon-education.svg';
import iconEvents from '@/assets/images/public-pages/trusted-partners-icon-events.svg';
import iconBrandVisibility from '@/assets/images/public-pages/trusted-partners-icon-brand-visibility.svg';
import iconRelationship from '@/assets/images/public-pages/trusted-partners-icon-relationship.svg';
import categoryLegal from '@/assets/images/public-pages/trusted-partners-category-legal.svg';
import categoryFinance from '@/assets/images/public-pages/trusted-partners-category-finance.svg';
import categoryTechnology from '@/assets/images/public-pages/trusted-partners-category-technology.svg';
import categoryMarketing from '@/assets/images/public-pages/trusted-partners-category-marketing.svg';
import trustedPartnersBenefitsHeader from '@/assets/images/public-pages/trusted-partners-benefits-header.png';
import trustedPartnersDifferenceBg from '@/assets/images/public-pages/trusted-partners-difference-bg.png';

/**
 * Fallback artwork for Home/Benefits sections, mirroring `resource-cover.ts`'s
 * placeholder pattern. These are the actual images already uploaded through
 * the CMS for each slot (pulled from the live S3 bucket, not stock photos),
 * so a fresh clone with an empty database shows the real approved visuals
 * immediately instead of a blank section - no admin upload required. The
 * moment an admin uploads a different image through the existing CMS field,
 * that value takes over and the default is never used for that slot again.
 */
export const DEFAULT_HERO_IMAGE: Partial<Record<string, string>> = {
  home: homeHero,
  benefits: benefitsHero,
  investors: investorsHero,
  'trusted-partners': trustedPartnersHero,
  // Due Diligence Platform has no hero image of its own yet (MYN-269) -
  // reuses Home's hero photo, per explicit instruction, rather than leaving
  // the hero without an image or introducing a new unapproved asset.
  'due-diligence-platform': homeHero,
};

/**
 * Investors' benefits-split section (FR-133) is a 3-photo collage in Figma -
 * two images side by side, one full-width below - not the single-image
 * layout every other `mission-split` usage has. Keyed by section key, same
 * as every other per-section default above.
 */
export const DEFAULT_INVESTOR_BENEFITS_IMAGES: Partial<Record<string, [string, string, string]>> = {
  'investor-benefits': [investorsBenefitsTopLeft, investorsBenefitsTopRight, investorsBenefitsBottom],
};

export const DEFAULT_CTA_BANNER_IMAGE: Partial<Record<string, string>> = {
  'ai-due-diligence': homeAiDueDiligence,
  'for-investors': homeForInvestors,
  'for-trusted-partners': homeForTrustedPartners,
};

export const DEFAULT_MISSION_SPLIT_IMAGE: Partial<Record<string, string>> = {
  'our-mission': homeOurMission,
};

export const DEFAULT_CHECKLIST_SPLIT_IMAGE: Partial<Record<string, string>> = {
  'prepare-with-purpose': benefitsPrepareWithPurpose,
};

/** Per-card defaults, keyed by section key then card index - only Home's 4-card grid needs one; Benefits' cards don't use images. */
export const DEFAULT_CARD_GRID_IMAGES: Partial<Record<string, string[]>> = {
  'fundraising-advantage': [homeFundraisingCard0, homeFundraisingCard1, homeFundraisingCard2, homeFundraisingCard3],
};

/**
 * `category-band`'s required-in-spirit background image (`02-design.md` §22's
 * implementation correction - the Zod field is optional, this bundled
 * fallback is what actually guarantees the section is never rendered blank).
 * Keyed by section key, same idiom as every other default above.
 */
export const DEFAULT_CATEGORY_BAND_IMAGE: Partial<Record<string, string>> = {
  'partner-categories': trustedPartnersCategoriesBg,
};

/**
 * Due Diligence Platform's "exclusive-workspaces" card-grid (MYN-269) has
 * only 2 cards against the component's 4-column desktop default, leaving a
 * visibly empty gap beside them. Rather than a plain grid change, a filler
 * image closes that gap the same way Home fills space beside copy elsewhere
 * (hero, cta-banner, mission-split) - reusing Home's own "Our Mission" photo
 * (a team reviewing documents together), a real photo thematically fitting
 * this section's legal-workspace content. `home-ai-due-diligence.png` was
 * tried first but is a flat placeholder rectangle, not an actual photo - see
 * its own file - so it read as empty rather than filling the gap.
 */
export const DEFAULT_CARD_GRID_FILLER_IMAGE: Partial<Record<string, string>> = {
  'exclusive-workspaces': homeOurMission,
};

/**
 * Small decorative icons for `card-grid`'s carousel layout (Trusted Partners'
 * Partners Benefits, FR-143) and `category-band`'s pills (FR-146). Unlike
 * every other map above, these are keyed by the CMS content's own `icon`
 * string value, not by section/page key - `icon` is presentational dressing,
 * not an S3-uploaded image, so it never goes through the presigned-URL flow
 * (`imageFields` has no entry for it in either Backend schema).
 */
export const CARD_ICON_BY_KEY: Partial<Record<string, string>> = {
  'co-branded': iconCoBranded,
  education: iconEducation,
  events: iconEvents,
  'brand-visibility': iconBrandVisibility,
  relationship: iconRelationship,
};

export const CATEGORY_ICON_BY_KEY: Partial<Record<string, string>> = {
  legal: categoryLegal,
  finance: categoryFinance,
  technology: categoryTechnology,
  marketing: categoryMarketing,
};

/** Photo beside Partners Benefits' header text (Figma's "Container" node) - found missing during manual review, 2026-10-08. */
export const DEFAULT_CARD_GRID_HEADER_IMAGE: Partial<Record<string, string>> = {
  'partners-benefits': trustedPartnersBenefitsHeader,
};

/** Full-bleed background photo behind "The Difference" section (Figma's "Business Background Image" node) - found missing during manual review, 2026-10-08. */
export const DEFAULT_COMPARISON_SPLIT_BACKGROUND: Partial<Record<string, string>> = {
  'the-difference': trustedPartnersDifferenceBg,
};

/**
 * Resolve the image to render for a section, falling back to its default
 * when the CMS content has none set.
 *
 * @param contentImage - the image key/URL from the CMS content, or empty/undefined when unset
 * @param defaultImage - the bundled fallback for this slot, or undefined if this slot has none
 * @returns the URL to put in `img src`, or undefined if neither exists
 */
export function imageOrDefault(contentImage: string | undefined, defaultImage: string | undefined): string | undefined {
  return contentImage?.trim() ? contentImage : defaultImage;
}
